import { useEffect } from "react";
import { Users2 } from "lucide-react";
import { SubSection } from "../SubSection";
import { NavItem } from "../NavItem";
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import LocalAtmOutlinedIcon from '@mui/icons-material/LocalAtmOutlined';
import FolderCopyOutlinedIcon from '@mui/icons-material/FolderCopyOutlined';
import { useLicenseRequestsStore } from "@/lib/license-requests";

interface CollaborateSectionProps {
  isCollapsed: boolean;
  currentPath: string;
  isBuyer?: boolean;
  isNegotiator?: boolean;
}

/**
 * Collaborate — where you trade. Rights ADMIN moved out on 2026-09-02 (see `RightsSection`):
 * Rights to Confirm and Your Companies now live in their own section above this one.
 *
 * License Requests deliberately STAYED. It is the deal surface — the thing you do once rights are
 * settled — not rights admin, and it is the only item here every role can see.
 *
 * The store fetch below stayed here with it. It feeds exactly one badge (License Requests), and
 * RightsSection reads nothing from this store, so there is still exactly one mount-time
 * `fetchStats()` in the sidebar and no second caller to race it.
 */
export function CollaborateSection({ isCollapsed, currentPath, isBuyer, isNegotiator }: CollaborateSectionProps) {
  const stats = useLicenseRequestsStore((s) => s.stats);
  const statsLoading = useLicenseRequestsStore((s) => s.statsLoading);
  // Show pending count as badge for sellers, or negotiating for buyers
  // Don't show "0" while stats are loading — show nothing until loaded
  const pendingCount = statsLoading ? 0 : isBuyer
    ? (stats?.negotiating ?? 0)
    : (stats?.pending ?? 0);

  // Fetch stats on mount if not loaded
  useEffect(() => {
    const store = useLicenseRequestsStore.getState();
    if (!store.stats) {
      store.fetchStats();
    }
  }, []);

  return (
    <SubSection title="Collaborate" icon={Users2} isCollapsed={isCollapsed}>
      {!isNegotiator ? (
        <NavItem
          icon={StorefrontOutlinedIcon}
          label="Storefronts"
          path="/marketplace"
          isCollapsed={isCollapsed}
          isActive={currentPath === "/marketplace"}
        />
      ) : null}
      {isBuyer ? (
        <NavItem
          icon={FolderCopyOutlinedIcon}
          label="Projects"
          path="/projects"
          isCollapsed={isCollapsed}
          isActive={currentPath.startsWith("/projects")}
        />
      ) : null}
      <NavItem
        icon={LocalAtmOutlinedIcon}
        label="License Requests"
        path="/licenses"
        isCollapsed={isCollapsed}
        isActive={currentPath.startsWith("/licenses")}
        badge={pendingCount > 0 ? String(pendingCount) : undefined}
      />
    </SubSection>
  );
}
